// chatDock — the chat lives in the right panel, never in the main view.
//
// Redesign step 1 (material-first): the main view shows the Library (or
// Journal, Settings, …) and the conversation sits in the docked chat panel
// beside it. The working set keeps conversations exactly as before
// (`conversationsOpen`), with ONE added invariant: the list is kept
// most-recently-entered LAST, and that last member is the conversation the
// chat panel shows. An active conversation tab is therefore a transient the
// frame fold resolves at once (`dockConversation`): the conversation moves to
// the panel and the main view goes back to what it showed before (or the
// Library when that is gone).
//
// Pure: TabSet in, TabSet out; same reference for a no-op.

export { chatConversationOf, dockConversation };

import { exhaustSwitchCase } from "@bilda/utils";
import {
    type ActiveTab,
    type ConversationId,
    openSearch,
    type TabSet,
} from "./types.js";

// The conversation the chat panel shows, or null when none is open.
const chatConversationOf = (tabs: TabSet): ConversationId | null =>
    tabs.conversationsOpen.at(-1) ?? null;

// Whether `tab` still names an open main-view tab in `tabs`. A conversation is
// never a main view, so it never counts.
const isOpenMainTab = (tabs: TabSet, tab: ActiveTab): boolean => {
    switch (tab.type) {
        case "resource":
            return tabs.open.some((t) => t.resource.id === tab.id);
        case "quality":
            return tabs.qualityOpen.some(
                (q) => q.materialId === tab.materialId,
            );
        case "settings":
            return tabs.settingsOpen;
        case "home":
            return tabs.homeOpen;
        case "studio":
            return tabs.studioOpen;
        case "projects":
            return tabs.projectsOpen;
        case "knowledge":
            return tabs.knowledgeOpen;
        case "search":
            return tabs.searchOpen;
        case "conversation":
            return false;
        default:
            return exhaustSwitchCase(tab);
    }
};

// Move an ACTIVE conversation into the chat panel (last in
// `conversationsOpen`) and give the main view back to `main` when it is still
// open, else to the Library. A no-op when no conversation is active.
const dockConversation = (tabs: TabSet, main: ActiveTab): TabSet => {
    if (tabs.active.type !== "conversation") return tabs;
    const { id } = tabs.active;
    const docked: TabSet = {
        ...tabs,
        conversationsOpen: [
            ...tabs.conversationsOpen.filter((c) => c !== id),
            id,
        ],
    };
    return isOpenMainTab(docked, main)
        ? { ...docked, active: main }
        : openSearch(docked);
};
